<script setup lang="ts">
import { CardType } from '~/types'
import { useEventBus } from '@vueuse/core'

const props = defineProps<{
  data: SectionProjectListRecord
  moduleIndex: number
}>()

const appConfig = useAppConfig()
const { isMobileOrTablet } = useDevice()

const { level, isSemantic, invertedStyles } = useSectionRules(
  props.moduleIndex,
  props.data.invertColorTheme,
  !!props.data.title
)

const { emit } = useEventBus<boolean>('fire-works')
const isAnimationTriggered = ref(false)
const sectionRef = ref<HTMLElement | null>(null)
const workAnimation = useState('workAnimation')

const { stop } = useIntersectionObserver(
  sectionRef,
  ([{ isIntersecting }], _observerElement) => {
    if (isIntersecting && !isAnimationTriggered.value) {
      isAnimationTriggered.value = true
      emit(isAnimationTriggered.value)
    }

    if (isIntersecting && !workAnimation.value) {
      workAnimation.value = true
    }
  },
  {
    threshold: 0.3
  }
)

onUnmounted(() => {
  stop()
})
</script>

<template>
  <div ref="sectionRef" :class="[invertedStyles]">
    <template v-if="data.layout === 'Canvas'">
      <div class="container relative py-12 lg:h-[1400px] lg:py-20">
        <MobileWorkGrid v-if="isMobileOrTablet" :data="data.records" />
        <CanvasWork
          v-if="!isMobileOrTablet"
          :enabled="appConfig.ui.enable3D"
          :data="data.records"
        />
        <CallToAction
          v-if="Array.isArray(data.buttons) && data.buttons.length"
          class="absolute bottom-12 left-1/2 -translate-x-1/2 lg:bottom-28"
          :data="data.buttons"
        />
      </div>
    </template>
    <template v-else-if="data.layout === 'Grid'">
      <div class="container relative py-12 lg:py-20">
        <div class="md:flex md:justify-between md:gap-10">
          <Heading
            v-if="data.title"
            :level="level"
            :size="4"
            class="mb-8 !font-black lg:mb-18"
            :is-semantic="isSemantic"
          >
            <TextReveal>{{ data.title }}</TextReveal>
          </Heading>
          <div>
            <CallToAction
              v-if="Array.isArray(data.buttons) && data.buttons.length"
              :data="data.buttons"
              class="hidden md:inline-block"
            />
          </div>
        </div>
        <WorkList :data="data.records" :is-loading="false" :type="CardType.WORK" />
        <CallToAction
          v-if="Array.isArray(data.buttons) && data.buttons.length"
          :data="data.buttons"
          class="mt-10 md:hidden"
        />
      </div>
    </template>
  </div>
</template>
